import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Plus, Search, MoreHorizontal, Pencil, Archive, Link2, Upload, X,
  ImageIcon, Check, LayoutGrid, List, ShoppingBag, Clock, Package,
  AlertTriangle, Trash2, Copy, ArchiveRestore, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/products")({
  head: () => ({
    meta: [
      { title: "Products — Spayzo Merchant" },
      { name: "description", content: "Manage rentable add-ons and items for sale." },
    ],
  }),
  component: ProductsPage,
});

type Kind = "rent" | "buy";
type Product = {
  id: string;
  nameKey: string;
  image: string;
  kind: Kind;
  price: number;
  qty: number;
  threshold: number;
  spaceKeys: string[];
  archived?: boolean;
};

const SPACE_KEYS = ["studioA", "meetingLoft", "photoBay", "podRoom", "rooftop"];

const seed: Product[] = [
  { id: "1", nameKey: "proLightingKit", image: "https://images.unsplash.com/photo-1606986628253-49e21e95bb50?w=600&q=80", kind: "rent", price: 18, qty: 4, threshold: 2, spaceKeys: ["photoBay", "studioA"] },
  { id: "2", nameKey: "coldBrewBottle", image: "https://images.unsplash.com/photo-1517701604599-bb29b565090c?w=600&q=80", kind: "buy", price: 5, qty: 36, threshold: 10, spaceKeys: ["studioA", "podRoom"] },
  { id: "3", nameKey: "webcam4k", image: "https://images.unsplash.com/photo-1587826080692-f439cd0b70da?w=600&q=80", kind: "rent", price: 6, qty: 1, threshold: 2, spaceKeys: ["meetingLoft"] },
  { id: "4", nameKey: "whiteboardMarkers", image: "https://images.unsplash.com/photo-1582731595330-cabd58c554b7?w=600&q=80", kind: "buy", price: 3, qty: 0, threshold: 5, spaceKeys: ["meetingLoft"] },
  { id: "5", nameKey: "tripodCarbon", image: "https://images.unsplash.com/photo-1606986628025-35d57e735ae0?w=600&q=80", kind: "rent", price: 8, qty: 6, threshold: 2, spaceKeys: ["photoBay"] },
  { id: "6", nameKey: "artisanCroissant", image: "https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=600&q=80", kind: "buy", price: 4, qty: 12, threshold: 6, spaceKeys: ["studioA", "rooftop"] },
];

type StockState = { labelKey: string; tone: string; dot: string };
function stockState(p: Product): StockState {
  if (p.qty === 0) return { labelKey: "outOfStock", tone: "bg-rose-500/10 text-rose-700 ring-rose-500/20", dot: "bg-rose-500" };
  if (p.qty <= p.threshold) return { labelKey: "lowStock", tone: "bg-amber-500/10 text-amber-700 ring-amber-500/20", dot: "bg-amber-500" };
  return { labelKey: "available", tone: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20", dot: "bg-emerald-500" };
}

function ProductsPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<Product[]>(seed);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<"all" | Kind>("all");
  const [stock, setStock] = useState<"all" | "available" | "low" | "out">("all");
  const [showArchived, setShowArchived] = useState(false);
  const [view, setView] = useState<"grid" | "list">("grid");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);

  const [assignTarget, setAssignTarget] = useState<{ ids: string[] } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ ids: string[] } | null>(null);

  const items = useMemo(() => {
    return data.filter((p) => {
      if (!showArchived && p.archived) return false;
      if (showArchived && !p.archived) return false;
      if (kind !== "all" && p.kind !== kind) return false;
      if (stock === "available" && !(p.qty > p.threshold)) return false;
      if (stock === "low" && !(p.qty > 0 && p.qty <= p.threshold)) return false;
      if (stock === "out" && p.qty !== 0) return false;
      if (q && !t(`merchantProducts.names.${p.nameKey}`).toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, q, kind, stock, showArchived, t]);

  const toggleSel = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  const allChecked = items.length > 0 && items.every((p) => selected.has(p.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (p: Product) => { setEditing(p); setOpen(true); };

  const upsert = (p: Product) => {
    setData((cur) => {
      const idx = cur.findIndex((x) => x.id === p.id);
      if (idx === -1) return [p, ...cur];
      const next = cur.slice();
      next[idx] = p;
      return next;
    });
  };

  const duplicate = (p: Product) => {
    const copy: Product = { ...p, id: `${p.id}-${Date.now()}`, archived: false };
    setData((cur) => [copy, ...cur]);
    toast.success(t("merchantProducts.toast.duplicated"));
  };

  const toggleArchive = (ids: string[]) => {
    setData((cur) =>
      cur.map((p) => (ids.includes(p.id) ? { ...p, archived: !p.archived } : p)),
    );
    setSelected(new Set());
    toast.success(ids.length > 1 ? t("merchantProducts.toast.bulkUpdated", { count: ids.length }) : t("merchantProducts.toast.updated"));
  };

  const remove = (ids: string[]) => {
    setData((cur) => cur.filter((p) => !ids.includes(p.id)));
    setSelected(new Set());
    toast.success(ids.length > 1 ? t("merchantProducts.toast.bulkDeleted", { count: ids.length }) : t("merchantProducts.toast.deleted"));
  };

  const applyAssign = (ids: string[], spaces: string[]) => {
    setData((cur) => cur.map((p) => (ids.includes(p.id) ? { ...p, spaceKeys: spaces } : p)));
    setSelected(new Set());
    toast.success(t("merchantProducts.toast.spacesUpdated"));
  };

  const counts = {
    available: data.filter((p) => !p.archived && p.qty > p.threshold).length,
    low: data.filter((p) => !p.archived && p.qty > 0 && p.qty <= p.threshold).length,
    out: data.filter((p) => !p.archived && p.qty === 0).length,
  };

  const selectedIds = Array.from(selected);
  const assignInitial = useMemo(() => {
    if (!assignTarget) return [];
    if (assignTarget.ids.length === 1) {
      return data.find((p) => p.id === assignTarget.ids[0])?.spaceKeys ?? [];
    }
    return [];
  }, [assignTarget, data]);

  return (
    <MerchantShell
      title={t("merchantProducts.title")}
      subtitle={t("merchantProducts.subtitle")}
      actions={
        <Button onClick={openCreate} className="h-10 rounded-xl shadow-soft">
          <Plus className="h-4 w-4" /> {t("merchantProducts.addProduct")}
        </Button>
      }
    >
      {/* Inventory summary */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StockCard label={t("merchantProducts.available")} value={counts.available} tone="emerald" />
        <StockCard label={t("merchantProducts.lowStock")} value={counts.low} tone="amber" />
        <StockCard label={t("merchantProducts.outOfStock")} value={counts.out} tone="rose" />
      </div>

      {/* Toolbar */}
      <Card className="mt-5 rounded-2xl border-border/60 p-3 shadow-soft sm:p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("merchantProducts.searchPlaceholder")}
              className="h-11 rounded-xl border-border/70 bg-surface ps-10"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
              <SelectTrigger aria-label={t("merchantProducts.allTypes")} className="h-11 w-36 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("merchantProducts.allTypes")}</SelectItem>
                <SelectItem value="rent">{t("merchantProducts.forRent")}</SelectItem>
                <SelectItem value="buy">{t("merchantProducts.forSale")}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stock} onValueChange={(v) => setStock(v as typeof stock)}>
              <SelectTrigger aria-label={t("merchantProducts.allStock")} className="h-11 w-40 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("merchantProducts.allStock")}</SelectItem>
                <SelectItem value="available">{t("merchantProducts.available")}</SelectItem>
                <SelectItem value="low">{t("merchantProducts.lowStock")}</SelectItem>
                <SelectItem value="out">{t("merchantProducts.outOfStock")}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showArchived ? "default" : "outline"}
              onClick={() => { setShowArchived((v) => !v); setSelected(new Set()); }}
              className="h-11 rounded-xl"
            >
              <Archive className="h-4 w-4" />
              {showArchived ? t("merchantProducts.archived") : t("merchantProducts.active")}
            </Button>
            <Tabs value={view} onValueChange={(v) => setView(v as typeof view)}>
              <TabsList className="h-11 rounded-xl">
                <TabsTrigger value="grid" aria-label={t("merchantProducts.gridView", "Grid view")} className="rounded-lg"><LayoutGrid className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="list" aria-label={t("merchantProducts.listView", "List view")} className="rounded-lg"><List className="h-4 w-4" /></TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-primary/5 px-3 py-2 text-sm">
            <span className="font-semibold">{t("merchantProducts.selectedCount", { count: selected.size })}</span>
            <span className="text-muted-foreground">·</span>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setAssignTarget({ ids: selectedIds })}>
              <Link2 className="h-3.5 w-3.5" /> {t("merchantProducts.assignSpaces")}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => toggleArchive(selectedIds)}>
              {showArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {showArchived ? t("merchantProducts.restore") : t("merchantProducts.archive")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-destructive hover:text-destructive"
              onClick={() => setDeleteTarget({ ids: selectedIds })}
            >
              <Trash2 className="h-3.5 w-3.5" /> {t("merchantProducts.delete")}
            </Button>
            <Button size="sm" variant="ghost" className="ms-auto h-8" onClick={() => setSelected(new Set())}>{t("merchantProducts.clear")}</Button>
          </div>
        )}
      </Card>

      {/* Items */}
      <div className="mt-5">
        {view === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((p) => (
              <ProductCard
                key={p.id}
                p={p}
                selected={selected.has(p.id)}
                onSelect={() => toggleSel(p.id)}
                onEdit={() => openEdit(p)}
                onAssign={() => setAssignTarget({ ids: [p.id] })}
                onDuplicate={() => duplicate(p)}
                onArchive={() => toggleArchive([p.id])}
                onDelete={() => setDeleteTarget({ ids: [p.id] })}
              />
            ))}
            {items.length === 0 && <EmptyState />}
          </div>
        ) : (
          <ProductTable
            items={items}
            allChecked={allChecked}
            toggleAll={toggleAll}
            selected={selected}
            onSelect={toggleSel}
            onEdit={openEdit}
            onAssign={(id) => setAssignTarget({ ids: [id] })}
            onDuplicate={duplicate}
            onArchive={(id) => toggleArchive([id])}
            onDelete={(id) => setDeleteTarget({ ids: [id] })}
          />
        )}
      </div>

      <ProductDialog
        open={open}
        onOpenChange={setOpen}
        product={editing}
        onSave={(p) => { upsert(p); setOpen(false); toast.success(editing ? t("merchantProducts.toast.saved") : t("merchantProducts.toast.created")); }}
      />

      <AssignSpacesDialog
        open={!!assignTarget}
        initial={assignInitial}
        count={assignTarget?.ids.length ?? 0}
        onOpenChange={(v) => { if (!v) setAssignTarget(null); }}
        onConfirm={(spaces) => {
          if (assignTarget) applyAssign(assignTarget.ids, spaces);
          setAssignTarget(null);
        }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTarget && deleteTarget.ids.length > 1 ? t("merchantProducts.deleteMultipleTitle", { count: deleteTarget.ids.length }) : t("merchantProducts.deleteSingleTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("merchantProducts.deleteDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("merchantProducts.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteTarget) remove(deleteTarget.ids); setDeleteTarget(null); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("merchantProducts.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MerchantShell>
  );
}

function StockCard({ label, value, tone }: { label: string; value: number; tone: "emerald" | "amber" | "rose" }) {
  const tones: Record<typeof tone, { bg: string; text: string; icon: React.ComponentType<{ className?: string }> }> = {
    emerald: { bg: "bg-emerald-500/10", text: "text-emerald-600", icon: Package },
    amber: { bg: "bg-amber-500/10", text: "text-amber-600", icon: AlertTriangle },
    rose: { bg: "bg-rose-500/10", text: "text-rose-600", icon: ShoppingBag },
  };
  const t = tones[tone];
  const Icon = t.icon;
  return (
    <Card className="rounded-2xl border-border/60 p-4 shadow-soft">
      <div className="flex items-center gap-3">
        <div className={cn("grid h-11 w-11 place-items-center rounded-xl", t.bg, t.text)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-display text-2xl font-bold tracking-tight">{value}</p>
        </div>
      </div>
    </Card>
  );
}

function KindPill({ kind }: { kind: Kind }) {
  const { t } = useTranslation();
  const cfg =
    kind === "rent"
      ? { label: t("merchantProducts.forRent"), icon: Clock, cls: "bg-sky-500/10 text-sky-700 ring-sky-500/20" }
      : { label: t("merchantProducts.forSale"), icon: ShoppingBag, cls: "bg-violet-500/10 text-violet-700 ring-violet-500/20" };
  const Icon = cfg.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ring-inset", cfg.cls)}>
      <Icon className="h-2.5 w-2.5" /> {cfg.label}
    </span>
  );
}

function ProductCard({
  p, selected, onSelect, onEdit, onAssign, onDuplicate, onArchive, onDelete,
}: {
  p: Product; selected: boolean; onSelect: () => void; onEdit: () => void;
  onAssign: () => void; onDuplicate: () => void; onArchive: () => void; onDelete: () => void;
}) {
  const { t } = useTranslation();
  const s = stockState(p);
  const name = t(`merchantProducts.names.${p.nameKey}`);
  return (
    <Card className={cn("group overflow-hidden rounded-2xl border-border/60 p-0 shadow-soft ring-1 ring-transparent transition duration-300 hover:-translate-y-0.5 hover:shadow-elevated hover:ring-primary/30", selected && "ring-2 ring-primary")}>
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        <img src={p.image} alt={name} className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-110" loading="lazy"
                decoding="async" />
        {/* gradient scrims for legible badges */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-black/25 opacity-90" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent" />
        {/* hover wash */}
        <div className="pointer-events-none absolute inset-0 bg-primary/0 transition-colors duration-300 group-hover:bg-primary/10" />
        <div className="absolute start-3 top-3 flex items-center gap-1.5">
          <Checkbox aria-label={t("merchantProducts.selectProduct", "Select product")} checked={selected} onCheckedChange={onSelect} className="border-white/40 bg-white/15 text-white backdrop-blur-md [&>svg]:text-white" />
          <KindPill kind={p.kind} />
          {p.archived && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white shadow-soft ring-1 ring-white/20 backdrop-blur-md">{t("merchantProducts.archived")}</span>
          )}
        </div>
        <span className={cn("absolute end-3 top-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset backdrop-blur-md", s.tone)}>
          <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
          {t(`merchantProducts.${s.labelKey}`)}
        </span>
        <div className="absolute bottom-3 end-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" aria-label={t("merchantProducts.actions", "Actions")} variant="secondary" className="h-8 w-8 rounded-full border-white/20 bg-white/15 text-white shadow-soft backdrop-blur-md hover:bg-white/25 hover:text-white">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onEdit}><Pencil className="h-4 w-4" /> {t("merchantProducts.edit")}</DropdownMenuItem>
              <DropdownMenuItem onClick={onAssign}><Link2 className="h-4 w-4" /> {t("merchantProducts.assignToSpaces")}</DropdownMenuItem>
              <DropdownMenuItem onClick={onDuplicate}><Copy className="h-4 w-4" /> {t("merchantProducts.duplicate")}</DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive}>
                {p.archived ? <><ArchiveRestore className="h-4 w-4" /> {t("merchantProducts.restore")}</> : <><Archive className="h-4 w-4" /> {t("merchantProducts.archive")}</>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                <Trash2 className="h-4 w-4" /> {t("merchantProducts.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <p className="truncate font-display text-base font-semibold">{name}</p>
          <div className="text-end">
            <p className="font-display text-lg font-bold tracking-tight"><Riyal /> {p.price}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {p.kind === "rent" ? t("merchantProducts.perHour") : t("merchantProducts.perItem")}
            </p>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t("merchantProducts.quantity")}</span>
          <span className="font-semibold">{p.qty} {p.kind === "rent" ? t("merchantProducts.units") : t("merchantProducts.inStock")}</span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full rounded-full", s.dot)}
            style={{ width: `${Math.min(100, (p.qty / Math.max(p.threshold * 3, 1)) * 100)}%` }}
          />
        </div>

        <div className="mt-3 flex flex-wrap gap-1">
          {p.spaceKeys.slice(0, 3).map((sp) => (
            <Badge key={sp} variant="secondary" className="rounded-full text-[10px]">{t(`merchantProducts.spaces.${sp}`)}</Badge>
          ))}
          {p.spaceKeys.length > 3 && (
            <Badge variant="secondary" className="rounded-full text-[10px]">+{p.spaceKeys.length - 3}</Badge>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={onEdit} className="h-9 flex-1 rounded-lg">
            <Pencil className="h-3.5 w-3.5" /> {t("merchantProducts.edit")}
          </Button>
          <Button size="sm" variant="ghost" onClick={onAssign} className="h-9 rounded-lg">
            <Link2 className="h-3.5 w-3.5" /> {t("merchantProducts.assign")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ProductTable({
  items, allChecked, toggleAll, selected, onSelect, onEdit, onAssign, onDuplicate, onArchive, onDelete,
}: {
  items: Product[];
  allChecked: boolean;
  toggleAll: () => void;
  selected: Set<string>;
  onSelect: (id: string) => void;
  onEdit: (p: Product) => void;
  onAssign: (id: string) => void;
  onDuplicate: (p: Product) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden rounded-2xl border-border/60 shadow-soft">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="w-10 p-3"><Checkbox aria-label={t("merchantProducts.selectAll", "Select all")} checked={allChecked} onCheckedChange={toggleAll} /></th>
              <th className="p-3 text-start">{t("merchantProducts.table.product")}</th>
              <th className="p-3 text-start">{t("merchantProducts.table.type")}</th>
              <th className="p-3 text-start">{t("merchantProducts.table.price")}</th>
              <th className="p-3 text-start">{t("merchantProducts.table.stock")}</th>
              <th className="p-3 text-start">{t("merchantProducts.table.spaces")}</th>
              <th className="w-10 p-3" />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const s = stockState(p);
              const name = t(`merchantProducts.names.${p.nameKey}`);
              return (
                <tr key={p.id} className="border-t border-border/60 hover:bg-surface/60">
                  <td className="p-3"><Checkbox aria-label={t("merchantProducts.selectProduct", "Select product")} checked={selected.has(p.id)} onCheckedChange={() => onSelect(p.id)} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={name} className="h-10 w-10 rounded-lg object-cover" />
                      <span className="font-medium">{name}</span>
                    </div>
                  </td>
                  <td className="p-3"><KindPill kind={p.kind} /></td>
                  <td className="p-3 font-semibold"><Riyal /> {p.price}<span className="ms-1 text-[10px] font-normal text-muted-foreground">/{p.kind === "rent" ? t("merchantProducts.hr") : t("merchantProducts.item")}</span></td>
                  <td className="p-3">
                    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset", s.tone)}>
                      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} /> {p.qty} · {t(`merchantProducts.${s.labelKey}`)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {p.spaceKeys.slice(0, 2).map((sp) => <Badge key={sp} variant="secondary" className="rounded-full text-[10px]">{t(`merchantProducts.spaces.${sp}`)}</Badge>)}
                      {p.spaceKeys.length > 2 && <Badge variant="secondary" className="rounded-full text-[10px]">+{p.spaceKeys.length - 2}</Badge>}
                    </div>
                  </td>
                  <td className="p-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="icon" aria-label={t("merchantProducts.actions", "Actions")} variant="ghost" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => onEdit(p)}><Pencil className="h-4 w-4" /> {t("merchantProducts.edit")}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onAssign(p.id)}><Link2 className="h-4 w-4" /> {t("merchantProducts.assignToSpaces")}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onDuplicate(p)}><Copy className="h-4 w-4" /> {t("merchantProducts.duplicate")}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onArchive(p.id)}>
                          {p.archived ? <><ArchiveRestore className="h-4 w-4" /> {t("merchantProducts.restore")}</> : <><Archive className="h-4 w-4" /> {t("merchantProducts.archive")}</>}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onDelete(p.id)} className="text-destructive focus:text-destructive">
                          <Trash2 className="h-4 w-4" /> {t("merchantProducts.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7}><EmptyState compact /></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function EmptyState({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={cn("rounded-2xl border border-dashed p-10 text-center", compact && "border-0")}>
      <p className="text-sm font-semibold">{t("merchantProducts.emptyTitle")}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t("merchantProducts.emptyDesc")}</p>
    </div>
  );
}

/* ------------------------ Assign Spaces Dialog ------------------------ */

function AssignSpacesDialog({
  open, onOpenChange, initial, count, onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: string[];
  count: number;
  onConfirm: (spaces: string[]) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(initial);
  useEffect(() => { if (open) setSelected(initial); }, [open, initial]);
  const toggle = (s: string) =>
    setSelected((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t("merchantProducts.assignDialog.title")}</DialogTitle>
          <DialogDescription>
            {count > 1 ? t("merchantProducts.assignDialog.applyingTo", { count }) : t("merchantProducts.assignDialog.chooseSpaces")}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between rounded-xl">
                <span className="truncate text-start">
                  {selected.length === 0
                    ? t("merchantProducts.assignDialog.selectSpaces")
                    : selected.length <= 2
                    ? selected.map((s) => t(`merchantProducts.spaces.${s}`)).join(", ")
                    : t("merchantProducts.assignDialog.spacesSelected", { count: selected.length })}
                </span>
                <ChevronDown className="h-4 w-4 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="max-h-64 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
            >
              {SPACE_KEYS.map((s) => (
                <DropdownMenuCheckboxItem
                  key={s}
                  checked={selected.includes(s)}
                  onCheckedChange={() => toggle(s)}
                  onSelect={(e: Event) => e.preventDefault()}
                >
                  {t(`merchantProducts.spaces.${s}`)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {t("merchantProducts.assignDialog.ofSelected", { count: selected.length, total: SPACE_KEYS.length })}
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">{t("merchantProducts.cancel")}</Button>
          <Button onClick={() => onConfirm(selected)} className="rounded-xl">{t("merchantProducts.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------ Product Dialog ------------------------ */

type Draft = {
  name: string;
  description: string;
  kind: Kind;
  price: number;
  qty: number;
  threshold: number;
  spaces: string[];
  image: string;
};

const DEFAULT_IMAGE = "https://images.unsplash.com/photo-1606986628253-49e21e95bb50?w=600&q=80";

function ProductDialog({
  open, onOpenChange, product, onSave,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  product: Product | null;
  onSave: (p: Product) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => ({
    name: "", description: "", kind: "rent", price: 10, qty: 1, threshold: 3, spaces: [], image: DEFAULT_IMAGE,
  }));

  useEffect(() => {
    if (!open) return;
    if (product) {
      setDraft({
        name: t(`merchantProducts.names.${product.nameKey}`),
        description: "",
        kind: product.kind,
        price: product.price,
        qty: product.qty,
        threshold: product.threshold,
        spaces: product.spaceKeys,
        image: product.image,
      });
    } else {
      setDraft({ name: "", description: "", kind: "rent", price: 10, qty: 1, threshold: 3, spaces: [], image: DEFAULT_IMAGE });
    }
  }, [open, product, t]);

  const patch = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleSpace = (s: string) =>
    patch("spaces", draft.spaces.includes(s) ? draft.spaces.filter((x) => x !== s) : [...draft.spaces, s]);

  const handleUpload = (files: FileList | null) => {
    const f = files?.[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    patch("image", url);
  };

  const submit = () => {
    if (!draft.name.trim()) { toast.error(t("merchantProducts.nameRequired")); return; }
    const p: Product = {
      id: product?.id ?? `p-${Date.now()}`,
      nameKey: product?.nameKey ?? `custom-${Date.now()}`,
      kind: draft.kind,
      price: Number(draft.price) || 0,
      qty: Number(draft.qty) || 0,
      threshold: Number(draft.threshold) || 0,
      spaceKeys: draft.spaces,
      image: draft.image,
      archived: product?.archived,
    };
    onSave(p);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border/60 p-6">
          <DialogTitle className="font-display text-xl">
            {product ? t("merchantProducts.editProduct") : t("merchantProducts.addProduct")}
          </DialogTitle>
          <DialogDescription>{t("merchantProducts.dialogSubtitle")}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 p-6 lg:grid-cols-5">
          {/* Left: images */}
          <div className="lg:col-span-2 space-y-4">
            <Field label={t("merchantProducts.images")}>
              <div className="grid grid-cols-3 gap-2">
                <div className="relative col-span-3 aspect-[4/3] overflow-hidden rounded-xl bg-muted">
                  <img src={draft.image} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => patch("image", DEFAULT_IMAGE)}
                    className="absolute end-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-background/90 shadow-soft"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                <label className="grid aspect-square cursor-pointer place-items-center rounded-xl border-2 border-dashed border-border bg-surface text-muted-foreground transition hover:border-primary hover:text-primary">
                  <Upload className="h-5 w-5" />
                  <input
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    onChange={(e) => handleUpload(e.target.files)}
                  />
                </label>
              </div>
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <ImageIcon className="h-3 w-3" /> {t("merchantProducts.imagesHint")}
              </p>
            </Field>

            <Field label={t("merchantProducts.type")}>
              <RadioGroup value={draft.kind} onValueChange={(v) => patch("kind", v as Kind)} className="grid grid-cols-2 gap-2">
                <KindOption value="rent" current={draft.kind} icon={Clock} title={t("merchantProducts.forRent")} subtitle={t("merchantProducts.hourlyBilling")} />
                <KindOption value="buy" current={draft.kind} icon={ShoppingBag} title={t("merchantProducts.forSale")} subtitle={t("merchantProducts.perItem")} />
              </RadioGroup>
            </Field>
          </div>

          {/* Right: details */}
          <div className="lg:col-span-3 space-y-4">
            <Field label={t("merchantProducts.productName")}>
              <Input value={draft.name} onChange={(e) => patch("name", e.target.value)} placeholder={t("merchantProducts.productNamePlaceholder")} className="h-11 rounded-xl" />
            </Field>
            <Field label={t("merchantProducts.description")}>
              <Textarea value={draft.description} onChange={(e) => patch("description", e.target.value)} placeholder={t("merchantProducts.descriptionPlaceholder")} className="min-h-24 rounded-xl" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={draft.kind === "rent" ? t("merchantProducts.pricePerHour") : t("merchantProducts.pricePerItem")}>
                <Input type="number" value={draft.price} onChange={(e) => patch("price", Number(e.target.value))} className="h-11 rounded-xl" />
              </Field>
              <Field label={draft.kind === "rent" ? t("merchantProducts.availableUnits") : t("merchantProducts.quantityInStock")}>
                <Input type="number" value={draft.qty} onChange={(e) => patch("qty", Number(e.target.value))} className="h-11 rounded-xl" />
              </Field>
            </div>
            <Field label={t("merchantProducts.lowStockAlert")}>
              <Input type="number" value={draft.threshold} onChange={(e) => patch("threshold", Number(e.target.value))} className="h-11 w-32 rounded-xl" />
            </Field>

            <Field label={t("merchantProducts.assignToSpaces")}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-11 w-full justify-between rounded-xl">
                    <span className="truncate text-start">
                      {draft.spaces.length === 0
                        ? t("merchantProducts.assignDialog.selectSpaces")
                        : draft.spaces.length <= 2
                        ? draft.spaces.map((s) => t(`merchantProducts.spaces.${s}`)).join(", ")
                        : t("merchantProducts.assignDialog.spacesSelected", { count: draft.spaces.length })}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="max-h-64 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
                >
                  {SPACE_KEYS.map((s) => (
                    <DropdownMenuCheckboxItem
                      key={s}
                      checked={draft.spaces.includes(s)}
                      onCheckedChange={() => toggleSpace(s)}
                      onSelect={(e: Event) => e.preventDefault()}
                    >
                      {t(`merchantProducts.spaces.${s}`)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <p className="mt-2 text-[11px] text-muted-foreground">{t("merchantProducts.assignDialog.ofSelected", { count: draft.spaces.length, total: SPACE_KEYS.length })}</p>
            </Field>
          </div>
        </div>

        <DialogFooter className="gap-2 border-t border-border/60 p-4 sm:p-6">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">{t("merchantProducts.cancel")}</Button>
          <Button onClick={submit} className="rounded-xl shadow-soft">
            {product ? t("merchantProducts.saveChanges") : t("merchantProducts.createProduct")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KindOption({
  value, current, icon: Icon, title, subtitle,
}: {
  value: Kind;
  current: Kind;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
}) {
  const active = value === current;
  return (
    <Label
      htmlFor={`kind-${value}`}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition",
        active ? "border-primary bg-primary/5 shadow-soft" : "border-border hover:border-primary/50",
      )}
    >
      <RadioGroupItem id={`kind-${value}`} value={value} className="mt-0.5" />
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        <p className="text-[11px] text-muted-foreground">{subtitle}</p>
      </div>
    </Label>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}
